import { contactItems, logoImages } from './brand.js';
import { h, icon, pageNonce } from './dom.js';
import { hash, load, save } from './store.js';
import { setMode } from './track.js';

// The parts around every page: the announcement, header, footer, the notice, and the ad bar for small screens.

/** A link from the settings. A path on the site, or an address elsewhere. */
export function linkAttrs(url, ctx) {
  if (url.startsWith('/')) return { href: ctx.href(url) };
  if (url.startsWith('#')) return { href: url };
  return { href: url, target: '_blank', rel: 'noopener noreferrer' };
}

export function announcement(ctx) {
  const { design } = ctx;
  const message = design['header.announcement'];
  if (!message || load('announce', '') === hash(message)) return null;
  const bar = h('div', { class: 'announce', role: 'region', 'aria-label': message });
  bar.append(design['header.announcement_link'] ? h('a', { ...linkAttrs(design['header.announcement_link'], ctx) }, message) : message);
  if (design['header.announcement_close']) {
    bar.append(h('button', { type: 'button', 'aria-label': ctx.text.t('close'), on: { click: () => (save('announce', hash(message)), bar.remove()) } }, icon('close')));
  }
  return bar;
}

/** The icon of a menu entry, from where it leads. */
const MENU_ICONS = { '/': 'home', '/browse': 'grid', '/rankings': 'trend', '/library': 'library', '/search': 'search' };
const iconFor = (url) => MENU_ICONS[url.replace(/\/$/, '') || '/'] ?? 'link';

/** Whether the menu is a sidebar (the default) or a bar across the top. */
export const sidebarMode = (design) => design['layout.nav'] !== 'top';

/**
 * The bar across the top. With the sidebar it holds the menu button (phones), the platform menu, the search box and the language
 * menu; without it, the name, the links and the same things.
 */
export function header(ctx) {
  const { design, site } = ctx;
  const side = sidebarMode(design);
  const logos = logoImages(design, site.name);
  const showName = logos.length === 0 || design['brand.show_name'];
  const nav = side ? null : h('nav', { class: 'nav', id: 'nav', 'aria-label': ctx.text.t('menu') }, (design['header.nav'] ?? []).map((l) => h('a', { ...linkAttrs(l.url, ctx), dataset: { path: l.url.startsWith('/') ? l.url : '' } }, l.label)));
  const toggle = h(
    'button',
    {
      class: 'menu-toggle',
      type: 'button',
      'aria-label': ctx.text.t('menu'),
      'aria-expanded': 'false',
      ...(side ? { 'aria-controls': 'sidebar' } : {}),
      on: { click: () => (side ? ctx.openSidebar(true) : toggle.setAttribute('aria-expanded', String(nav.classList.toggle('open')))) },
    },
    icon('menu'),
  );

  let form = null;
  if (design['header.search']) {
    const input = h('input', { type: 'search', name: 'q', placeholder: ctx.text.t('search_placeholder'), 'aria-label': ctx.text.t('search_button'), minlength: 2, maxlength: 80, autocomplete: 'off' });
    form = h(
      'form',
      {
        class: 'search',
        role: 'search',
        on: {
          submit: (e) => {
            e.preventDefault();
            const q = input.value.trim();
            if (q.length < 2) return;
            // A platform chosen in the platform menu narrows the search too.
            const platform = design['browse.platform_filter'] ? new URLSearchParams(location.search).get('platform') : null;
            ctx.router.go(ctx.href('/search') + '?q=' + encodeURIComponent(q) + (platform ? '&platform=' + encodeURIComponent(platform) : ''));
          },
        },
      },
      input,
      h('button', { type: 'submit', 'aria-label': ctx.text.t('search_button') }, icon('search')),
    );
    ctx.searchInput = input;
  }

  // The language menu, when the site is offered in more than one. Choosing one goes to the same page in that language.
  const menu =
    ctx.languages.length > 1 && design['header.languages']
      ? h(
          'select',
          {
            class: 'lang',
            'aria-label': ctx.text.t('language'),
            on: {
              change: (e) => {
                const code = e.target.value;
                save('lang', code);
                const rest = location.pathname.slice(ctx.root.length);
                location.assign((code === ctx.first ? ctx.mount : ctx.mount + code + '/') + rest + location.search);
              },
            },
          },
          ctx.languages.map((l) => h('option', { value: l.code, selected: l.code === ctx.lang }, l.name)),
        )
      : null;

  // On a phone the sidebar is out of sight, so the name still has to be somewhere: a small one in this bar.
  const mini = side ? h('a', { class: 'brand-mini', href: ctx.href('/'), 'aria-label': site.name }, ...logoImages(design, ''), showName ? site.name : null) : null;
  const el = h(
    'header',
    { class: `header ${design['header.sticky'] ? 'sticky' : ''}` },
    h(
      'div',
      { class: 'wrap' },
      side ? mini : h('a', { class: 'brand', href: ctx.href('/'), 'aria-label': site.name }, ...logoImages(design, showName ? '' : site.name), showName ? site.name : null),
      nav,
      form,
      menu,
      toggle,
    ),
  );
  // Marks the menu entry for the page being shown.
  el.setActive = (path) => {
    if (!nav) return;
    for (const a of nav.querySelectorAll('a')) {
      const p = a.dataset.path;
      const on = p && (p === '/' ? path === '/' : path === p || path.startsWith(p + '/'));
      if (on) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    }
    nav.classList.remove('open');
    toggle.setAttribute('aria-expanded', 'false');
  };
  return el;
}

/** What the copyright line says, with the year and names filled in. */
export function copyLine(ctx) {
  const { design, site } = ctx;
  const company = design['brand.company'] || site.name;
  return String(design['footer.copyright'] ?? '').replaceAll('{year}', String(new Date().getFullYear())).replaceAll('{company}', company).replaceAll('{name}', site.name);
}

/**
 * The sidebar: the name, the menu links with their icons, a list of genres, and at the bottom the policy pages and the copyright line.
 * On a computer it stands at the left; on a phone it slides out from the menu button and closes on a choice, outside it or Escape.
 */
export function sidebar(ctx) {
  const { design, site, text } = ctx;
  const logos = logoImages(design, '');
  const showName = logos.length === 0 || design['brand.show_name'];
  const close = h('button', { class: 'side-close', type: 'button', 'aria-label': text.t('close'), on: { click: () => ctx.openSidebar(false) } }, icon('close'));
  const brand = h('a', { class: 'brand', href: ctx.href('/'), 'aria-label': site.name }, ...logoImages(design, showName ? '' : site.name), showName ? site.name : null);
  const nav = h(
    'nav',
    { class: 'side-nav', id: 'nav', 'aria-label': text.t('menu') },
    (design['header.nav'] ?? []).map((l) => h('a', { ...linkAttrs(l.url, ctx), dataset: { path: l.url.startsWith('/') ? l.url : '' } }, icon(iconFor(l.url)), h('span', {}, l.label))),
  );
  const genres = h('div', { class: 'side-genres' });
  const foot = h('div', { class: 'side-foot' });

  const mode = design['sidebar.genres'] ?? 'auto';
  if (mode !== 'off') {
    const show = (names) => {
      if (!names.length) return;
      genres.replaceChildren(
        h('h2', { class: 'side-title' }, text.t('sidebar_genres')),
        h('ul', {}, names.map((n) => h('li', {}, h('a', { href: ctx.href('/browse') + '?genre=' + encodeURIComponent(n), dataset: { genre: n } }, icon('hash'), h('span', {}, n))))),
      );
      genres.current?.();
    };
    if (mode === 'custom') show(String(design['sidebar.genre_list'] ?? '').split('\n').map((n) => n.trim()).filter(Boolean).slice(0, 30));
    else ctx.api.get('v1/facets').then((f) => show((f.genres ?? []).slice(0, Math.min(Math.max(Number(design['sidebar.genre_count']) || 8, 3), 30)).map((g) => g.value))).catch(() => {});
  }

  if (design['sidebar.footer']) {
    const own = design['footer.pages'] ? ctx.pages.filter((p) => p.in_footer) : [];
    const copy = copyLine(ctx);
    if (own.length) foot.append(h('p', { class: 'side-links' }, own.flatMap((p, i) => [i ? ' | ' : null, h('a', { href: ctx.href(`/page/${p.slug}`) }, p.title)])));
    if (copy) foot.append(h('p', {}, copy));
  }

  const el = h('aside', { class: 'sidebar', id: 'sidebar', 'aria-label': site.name }, close, brand, design['brand.tagline'] ? h('p', { class: 'side-tagline' }, design['brand.tagline']) : null, nav, genres, foot);
  // Escape closes it on a phone, and a link inside closes it once the page has changed.
  el.addEventListener('keydown', (e) => e.key === 'Escape' && ctx.openSidebar(false, true));
  el.setActive = (path, search) => {
    for (const a of nav.querySelectorAll('a')) {
      const p = a.dataset.path;
      const on = p && (p === '/' ? path === '/' : path === p || path.startsWith(p + '/'));
      if (on) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    }
    const chosen = path === '/browse' ? new URLSearchParams(search).get('genre') : null;
    genres.current = () => {
      for (const a of genres.querySelectorAll('a')) (a.dataset.genre === chosen ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current'));
    };
    genres.current();
    ctx.openSidebar(false);
  };
  return el;
}

export function footer(ctx) {
  const { design, site, pages } = ctx;
  // With the sidebar showing the policy pages and the copyright line, the footer does not say them again.
  const inSide = sidebarMode(design) && design['sidebar.footer'];
  const copy = inSide ? '' : copyLine(ctx);
  const links = [...(design['footer.links'] ?? [])].map((l) => ({ label: l.label, attrs: linkAttrs(l.url, ctx) }));
  const own = !inSide && design['footer.pages'] ? pages.filter((p) => p.in_footer).map((p) => ({ label: p.title, attrs: { href: ctx.href(`/page/${p.slug}`) } })) : [];
  const social = design['footer.social'] ?? [];
  const list = (title, items) => (items.length ? h('div', {}, h('h2', {}, title), h('ul', {}, items.map((i) => h('li', {}, h('a', i.attrs, i.label))))) : null);

  // Contact details, then a link to a help page if the site has one.
  const contact = design['contact.in_footer'] ? contactItems(design) : [];
  const help = design['contact.link'];
  const contactColumn =
    contact.length || help
      ? h(
          'div',
          {},
          h('h2', {}, ctx.text.t('contact')),
          h(
            'ul',
            {},
            contact.map((c) => h('li', {}, c.href ? h('a', { href: c.href, ...(c.kind === 'whatsapp' ? { target: '_blank', rel: 'noopener noreferrer' } : {}) }, c.text) : c.kind === 'address' ? h('address', {}, c.text) : h('span', { class: 'muted' }, c.text))),
            help ? h('li', {}, h('a', linkAttrs(help, ctx), ctx.text.t('contact'))) : null,
          ),
        )
      : null;

  const others = [list(site.name, [...own, ...links]), contactColumn, list(ctx.text.t('follow'), social.map((l) => ({ label: l.label, attrs: linkAttrs(l.url, ctx) })))].filter(Boolean);
  // Only the name and nothing else to say, beside a sidebar that already shows the name: no columns at all.
  const columns = inSide && !design['footer.about'] && !others.length ? null : h('div', { class: 'cols' }, h('div', {}, h('div', { class: 'brand' }, ...logoImages(design, ''), logoImages(design).length === 0 || design['brand.show_name'] ? site.name : null), design['footer.about'] ? h('p', { class: 'muted' }, design['footer.about']) : null), ...others);
  return h(
    'footer',
    { class: 'footer' },
    h(
      'div',
      { class: 'wrap' },
      columns,
      copy || ctx.consent.asks
        ? h(
            'div',
            { class: 'copy' },
            copy,
            ctx.consent.asks
              ? [
                  ' ',
                  h(
                    'button',
                    {
                      class: 'linkbtn',
                      type: 'button',
                      on: {
                        click: () => {
                          const el = notice(ctx, true);
                          if (el) document.body.append(el);
                        },
                      },
                    },
                    ctx.text.t('cookie_settings'),
                  ),
                ]
              : null,
          )
        : null,
    ),
  );
}

/** The notice to visitors with granular cookie categories and preference controls. */
export function notice(ctx, force = false) {
  const { design, consent } = ctx;
  const message = design['notice.text'];
  if (!design['notice.enabled'] || !message) return null;
  if (!force && (consent.asks ? consent.choice() !== null : load('notice', '') === hash(message))) return null;
  document.querySelector('.notice')?.remove();

  const title = design['notice.title'] || 'We use cookies';
  const bar = h('div', { class: 'notice', role: 'dialog', 'aria-label': title || message });

  // Current or default preferences
  const prefs = consent.preferences ? consent.preferences() : { essential: true, analytics: true, ads: true };

  // Interactive tickable checkboxes
  const essentialCheck = h('input', { type: 'checkbox', checked: true, disabled: true });
  const analyticsCheck = h('input', { type: 'checkbox', class: 'notice-check-analytics' });
  analyticsCheck.checked = prefs.analytics;
  const adsCheck = h('input', { type: 'checkbox', class: 'notice-check-ads' });
  adsCheck.checked = prefs.ads;

  const categories = h(
    'div',
    { class: 'notice-categories' },
    h(
      'label',
      { class: 'notice-cat' },
      essentialCheck,
      h(
        'span',
        { class: 'notice-cat-info' },
        h('span', { class: 'notice-cat-title' }, 'Essential'),
        h('span', { class: 'notice-cat-desc' }, 'Required for site features & playback progress'),
      ),
    ),
    h(
      'label',
      { class: 'notice-cat' },
      analyticsCheck,
      h(
        'span',
        { class: 'notice-cat-info' },
        h('span', { class: 'notice-cat-title' }, 'Analytics'),
        h('span', { class: 'notice-cat-desc' }, 'Understand visitor traffic & improve experience'),
      ),
    ),
    h(
      'label',
      { class: 'notice-cat' },
      adsCheck,
      h(
        'span',
        { class: 'notice-cat-info' },
        h('span', { class: 'notice-cat-title' }, 'Advertising'),
        h('span', { class: 'notice-cat-desc' }, 'Support free dramas with relevant promotions'),
      ),
    ),
  );

  const answer = (choice) => {
    bar.remove();
    if (consent.asks) {
      if (choice === 'accepted') {
        consent.set('accepted');
      } else if (choice === 'declined') {
        consent.set('declined');
      } else if (choice === 'custom') {
        consent.set('custom', {
          analytics: analyticsCheck.checked,
          ads: adsCheck.checked,
        });
      }
    } else {
      save('notice', hash(message));
    }
  };

  const actionButtons = [];
  if (consent.asks) {
    actionButtons.push(
      h(
        'button',
        {
          class: 'btn',
          type: 'button',
          on: { click: () => answer('declined') },
        },
        design['notice.decline'] || 'Essential only',
      ),
      h(
        'button',
        {
          class: 'btn',
          type: 'button',
          style: 'border-color: var(--accent); color: var(--accent);',
          on: { click: () => answer('custom') },
        },
        'Save preferences',
      ),
      h(
        'button',
        {
          class: 'btn primary',
          type: 'button',
          on: {
            click: () => {
              analyticsCheck.checked = true;
              adsCheck.checked = true;
              answer('accepted');
            },
          },
        },
        design['notice.button'] || 'Accept all',
      ),
    );
  } else {
    actionButtons.push(
      h(
        'button',
        {
          class: 'btn primary',
          type: 'button',
          on: { click: () => answer('accepted') },
        },
        design['notice.button'] || 'Got it',
      ),
    );
  }

  bar.append(
    title ? h('h2', { class: 'notice-title' }, title) : null,
    h('p', {}, message, ' ', design['notice.link'] && design['notice.link_label'] ? h('a', linkAttrs(design['notice.link'], ctx), design['notice.link_label']) : null),
    consent.asks ? categories : null,
    h('div', { class: 'actions' }, ...actionButtons),
  );

  return bar;
}

/**
 * Statistics, only if the site was given an ID for them, and never inside the dashboard's preview (so staff looking at the site do not
 * count as visitors). The IDs are checked again here, so a setting can never become a script address. Tag Manager, when set,
 * takes over from the direct Analytics ID so a visit is not counted twice.
 */
export function analytics(design) {
  if (window.top !== window) return;

  const rawGtm = typeof design['seo.gtm'] === 'string' ? design['seo.gtm'].trim() : '';
  const rawGa = typeof design['seo.ga4'] === 'string' ? design['seo.ga4'].trim() : '';

  // 1. Google Tag Manager Container (strictly GTM-XXXXXXX)
  const isGtmContainer = /^GTM-[A-Z0-9]{4,14}$/i.test(rawGtm);
  if (isGtmContainer) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    if (!document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${rawGtm}"]`)) {
      document.head.append(h('script', { async: true, nonce: pageNonce(), src: `https://www.googletagmanager.com/gtm.js?id=${rawGtm}` }));
    }
    setMode('gtm');
  }

  // 2. Google Tag / Google Analytics 4 (G-XXXXX, GT-XXXXX, AW-XXXXX)
  const tagIds = [];
  const gPattern = /^(G|GT|AW)-[A-Z0-9]{4,16}$/i;
  if (gPattern.test(rawGa)) tagIds.push(rawGa.toUpperCase());
  // If the gtm field was provided with a Google Tag (e.g. GT-...) rather than a GTM container, treat it as a Google Tag!
  if (!isGtmContainer && gPattern.test(rawGtm)) {
    const id = rawGtm.toUpperCase();
    if (!tagIds.includes(id)) tagIds.push(id);
  }

  if (tagIds.length > 0) {
    const primaryId = tagIds.find((id) => id.startsWith('GT-')) || tagIds[0];
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());

    for (const id of tagIds) {
      window.gtag('config', id, { send_page_view: true });
    }

    if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${primaryId}"]`)) {
      document.head.append(h('script', {
        async: true,
        nonce: pageNonce(),
        src: `https://www.googletagmanager.com/gtag/js?id=${primaryId}`,
      }));
    }

    if (!isGtmContainer) {
      setMode('gtag');
    }
  }

  // 3. Plausible Analytics
  const pl = design['seo.plausible'];
  if (typeof pl === 'string' && /^[a-z0-9.-]{3,100}$/i.test(pl.trim())) {
    document.head.append(h('script', { defer: true, nonce: pageNonce(), src: 'https://plausible.io/js/script.js', 'data-domain': pl.trim() }));
    setMode(null, true);
  }
}

/**
 * The link to the ad-free page, in the header and the footer as the site chose, when the site sells ad-free access.
 * A browser that already has it sees a small "Ad-free" mark instead, which leads to the same page.
 */
export function offerLinks(ctx, offer) {
  for (const old of document.querySelectorAll('.offer-link')) old.remove();
  const where = ctx.design['subscribe.link'] ?? 'both';
  if (!offer || (!offer.enabled && !offer.subscribed) || where === 'none') return;
  const link = (extra) => h('a', { class: `offer-link ${extra}`, href: ctx.href('/subscribe') }, offer.subscribed ? ctx.text.t('ad_free') : ctx.text.t('remove_ads'));
  if (where === 'both' || where === 'header') document.querySelector('.header .wrap')?.insertBefore(link('in-header'), document.querySelector('.header .menu-toggle'));
  if (where === 'both' || where === 'footer') document.querySelector('.footer .wrap')?.append(h('div', { class: 'offer-link in-footer' }, link('')));
}
